import React, { useEffect, useRef } from 'react';

import classNames from 'classnames';

import Typography from '../../Typography/index.js';

export interface CheckboxProps
  extends Omit<React.ComponentPropsWithoutRef<'input'>, 'type'> {
  /**
   * A label to pass to the checkbox element.
   */
  label: string;

  /**
   * If true, the checkbox will be shown in an indeterminate state.
   * @default false
   */
  indeterminate?: boolean;

  /**
   * If true, the input and font-size will be smaller.
   * @default false
   */
  small?: boolean;
}

/**
 * Checkboxes are used when you have multiple options that a user can select
 * (zero, one, or multiple). The zero state for a set of checkboxes will always
 * be unselected.
 *
 * Regular checkboxes are used in consumer facing products without an admin
 * interface, such as our patient apps & scheduling apps.
 *
 * Small checkbox are used in our admin, staff, and analyst apps. These will
 * be used more commonly inside our tooling.
 *
 * @see https://www.figma.com/file/LdhxwbVqKRRLpyK5ekr4Q8/Component-Library?node-id=1902%3A1274
 */
const Checkbox = React.forwardRef(
  (
    {
      className,
      label,
      small = false,
      indeterminate = false,
      ...rest
    }: CheckboxProps,
    ref: React.ForwardedRef<HTMLLabelElement>,
  ) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    // Indeterminate can only be set via the DOM, so we can't pass as a prop
    useEffect(() => {
      if (!!inputRef.current) {
        inputRef.current!.indeterminate = indeterminate as boolean;
      }
    }, [inputRef, indeterminate]);

    return (
      <label ref={ref}>
        <input
          type="checkbox"
          className={classNames('checkbox', className)}
          {...rest}
          ref={inputRef}
        />
        <Typography
          variant={small ? 'title-medium' : 'title-large'}
          className={classNames('checkbox-label', {
            'size-small': small,
          })}
        >
          {label}
        </Typography>
      </label>
    );
  },
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
